import { useState, type ReactNode } from "react";
import type { CortexDrawerMode } from "@via/context/cortex-context";
import { classNames } from "@via/utils/helpers";

const MODES: Array<{ mode: CortexDrawerMode; label: string }> = [
  { mode: "insight", label: "AI Insight" },
  { mode: "claude", label: "Claude Code" },
];

/**
 * What the drawer shows for the person's flags. Claude Code mode (agent-6-2) needs only
 * FF_CLAUDE_CHANNEL; with one of the two flags on, the drawer shows that mode and no toggle.
 */
export const drawerModeFor = (
  featureFlags: Record<string, boolean | undefined> | undefined,
  chosen: CortexDrawerMode
): { mode: CortexDrawerMode; showToggle: boolean } => {
  const insight = !!featureFlags?.FF_AI_REPORT_INTERPRET;
  const claude = !!featureFlags?.FF_CLAUDE_CHANNEL;
  if (!claude) return { mode: "insight", showToggle: false };
  if (!insight) return { mode: "claude", showToggle: false };
  return { mode: chosen, showToggle: true };
};

export const DrawerModeToggle = ({
  mode,
  onChange,
}: {
  mode: CortexDrawerMode;
  onChange: (mode: CortexDrawerMode) => void;
}) => (
  <div
    role="tablist"
    aria-label="Drawer mode"
    className="mx-4 mt-3 inline-flex self-start rounded-lg bg-gray-100 p-0.5">
    {MODES.map(m => (
      <button
        key={m.mode}
        type="button"
        role="tab"
        aria-selected={mode === m.mode}
        onClick={() => onChange(m.mode)}
        className={classNames(
          "rounded-md px-3 py-1 text-xs font-semibold transition-colors",
          mode === m.mode
            ? "bg-white text-indigo-700 shadow-sm"
            : "text-gray-600 hover:text-gray-900"
        )}>
        {m.label}
      </button>
    ))}
  </div>
);

/**
 * Both bodies stay mounted once shown, and the inactive one is hidden, so switching modes never
 * resets AI Insight's open conversation, model or input, and never drops the Claude Code socket
 * state. The Claude Code body mounts on first use only.
 */
export const DrawerModeBodies = ({
  mode,
  insight,
  claude,
}: {
  mode: CortexDrawerMode;
  insight: ReactNode;
  claude: ReactNode;
}) => {
  const [claudeMounted, setClaudeMounted] = useState(mode === "claude");
  if (mode === "claude" && !claudeMounted) setClaudeMounted(true);
  return (
    <>
      <div
        data-testid="drawer-body-insight"
        hidden={mode !== "insight"}
        className={classNames(
          "min-h-0 flex-1 flex-col",
          mode === "insight" ? "flex" : "hidden"
        )}>
        {insight}
      </div>
      {claudeMounted && (
        <div
          data-testid="drawer-body-claude"
          hidden={mode !== "claude"}
          className={classNames(
            "min-h-0 flex-1 flex-col",
            mode === "claude" ? "flex" : "hidden"
          )}>
          {claude}
        </div>
      )}
    </>
  );
};
